import React, { useState } from 'react'
import {
    Box,
    Button,
    Container,
    Dialog,
    Fade,
    LinearProgress,
    Stack,
    Tooltip,
} from '@mui/material'
import Slide from '@mui/material/Slide'
import LanguageIcon from '@mui/icons-material/Language'
import desktoplogo from '../assets/logo-mobile-ldir.svg'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import CheckIcon from '@mui/icons-material/Check'
import { useInnsynContext } from '../context/InnsynContextProvider'
import { useNavigate } from 'react-router-dom'
import { INNSYN_SEARCH_PATH } from '../paths'
import { TransitionProps } from '@mui/material/transitions/transition'
import HeaderText from './HeaderText'
import HeaderCloseLine from './HeaderCloseLine'
import HeaderText2 from './HeaderText2'
import { FormattedMessage } from 'react-intl'
import { LOCALES } from '../i18n/locales'

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction='down' ref={ref} {...props} />
})

export const LogoHeader = () => {
    const {
        isLoadingGeografi,
        isLoadingMerke,
        isLoadingSnitt,
        isLoadingBruker,
        isLoadingMessages,
        brukerInformasjon,
        fetchBruker,
        valgtMaalform,
        setValgtMaalform,
    } = useInnsynContext()
    const navigate = useNavigate()
    const [dialogSprakIsOpen, setDialogSprakIsOpen] = useState(false)
    const [dialogBrukerIsOpen, setDialogBrukerIsOpen] = useState(false)

    const getProgress = () => {
        const dependencies = 5 // Number of elements loading. Update this if adding more loaders.
        const initialProgress = 10
        let progress = initialProgress
        if (!isLoadingGeografi) {
            progress += 100 / dependencies - initialProgress / dependencies
        }
        if (!isLoadingMerke) {
            progress += 100 / dependencies - initialProgress / dependencies
        }
        if (!isLoadingSnitt) {
            progress += 100 / dependencies - initialProgress / dependencies
        }
        if (!isLoadingBruker) {
            progress += 100 / dependencies - initialProgress / dependencies
        }
        if (!isLoadingMessages) {
            progress += 100 / dependencies - initialProgress / dependencies
        }
        return progress
    }

    const handleSpraakClick = (nyMaalform: LOCALES) => {
        setDialogSprakIsOpen(valgtMaalform === nyMaalform)
        setValgtMaalform(nyMaalform)
    }

    return (
        <Container
            sx={{
                borderBottom: '1px solid #d9d9d6',
            }}>
            <Stack
                direction='row'
                justifyContent='space-between'
                sx={{
                    width: '100%',
                    alignItems: 'center',
                    margin:
                        window.screen.width > 1300 ? '0.75rem 0' : '0.5rem 0',
                }}>
                <Tooltip arrow title='Hjem' enterDelay={1200}>
                    <Box sx={{ justifyContent: 'flex-start' }}>
                        <Box
                            onClick={() => {
                                navigate(INNSYN_SEARCH_PATH, { replace: true })
                            }}
                            component='img'
                            sx={{
                                '&:hover': {
                                    cursor: 'pointer',
                                },
                                height:
                                    window.screen.width > 1300
                                        ? '3.5rem'
                                        : '2rem',
                                width:
                                    window.screen.width > 1300
                                        ? '5rem'
                                        : '4rem',
                            }}
                            alt='Logo'
                            src={desktoplogo}
                        />
                    </Box>
                </Tooltip>
                <Box>
                    <Tooltip arrow title='Velg språk' enterDelay={1200}>
                        <Button
                            sx={{ fontSize: '1rem', color: 'common.black' }}
                            id='velg-spraak'
                            variant='text'
                            onClick={() => {
                                setDialogSprakIsOpen(true)
                            }}
                            endIcon={<LanguageIcon />}>
                            <FormattedMessage
                                id={'component.header.language'}
                            />
                        </Button>
                    </Tooltip>
                    <Tooltip arrow title='Logg på' enterDelay={1200}>
                        <Button
                            variant='text'
                            id='log-on'
                            onClick={() => {
                                setDialogBrukerIsOpen(true)
                            }}>
                            <AccountCircleOutlinedIcon />
                        </Button>
                    </Tooltip>
                </Box>
            </Stack>
            <Dialog
                aria-labelledby='velg-spraak'
                fullScreen
                open={dialogSprakIsOpen}
                onClose={() => {
                    setDialogSprakIsOpen(false)
                }}
                TransitionComponent={Transition}>
                <Box height='100%'>
                    <HeaderCloseLine closeFunction={setDialogSprakIsOpen} />
                    <Box
                        sx={{
                            height: '100%',
                            color: 'common.white',
                            paddingTop: '3rem',
                            paddingLeft: '5rem',
                            paddingRight: '1rem',
                            backgroundColor: '#35271E',
                        }}>
                        <HeaderText2
                            iconLeft={
                                valgtMaalform === LOCALES.BOKMAAL && (
                                    <CheckIcon
                                        sx={{
                                            marginRight: '1rem',
                                            marginLeft: '-2.5rem',
                                            color: 'common.white',
                                        }}
                                    />
                                )
                            }
                            highlighted={valgtMaalform === LOCALES.BOKMAAL}
                            header='Norsk Bokmål'
                            content='Alt innhold er tilgjengelig på bokmål'
                            onClick={() => handleSpraakClick(LOCALES.BOKMAAL)}
                            outline
                        />
                        <HeaderText2
                            iconLeft={
                                valgtMaalform === LOCALES.SOERSAMISK && (
                                    <CheckIcon
                                        sx={{
                                            marginRight: '1rem',
                                            marginLeft: '-2.5rem',
                                            color: 'common.white',
                                        }}
                                    />
                                )
                            }
                            highlighted={valgtMaalform === LOCALES.SOERSAMISK}
                            header='Sørsamisk'
                            content='Alt innhold er tilgjengelig på sørsamisk'
                            onClick={() =>
                                handleSpraakClick(LOCALES.SOERSAMISK)
                            }
                            outline
                        />
                        <HeaderText2
                            iconLeft={
                                valgtMaalform === LOCALES.NORDSAMISK && (
                                    <CheckIcon
                                        sx={{
                                            marginRight: '1rem',
                                            marginLeft: '-2.5rem',
                                            color: 'common.white',
                                        }}
                                    />
                                )
                            }
                            highlighted={valgtMaalform === LOCALES.NORDSAMISK}
                            header='Nordsamisk'
                            content='Alt innhold er tilgjengelig på nordsamisk'
                            onClick={() =>
                                handleSpraakClick(LOCALES.NORDSAMISK)
                            }
                            outline
                        />
                    </Box>
                </Box>
            </Dialog>

            <Dialog
                aria-labelledby='log-on'
                fullScreen
                open={dialogBrukerIsOpen}
                onClose={() => {
                    setDialogBrukerIsOpen(false)
                }}
                TransitionComponent={Transition}>
                <Box height='100%'>
                    <HeaderCloseLine
                        closeFunction={(bool: boolean) => {
                            setDialogBrukerIsOpen(bool)
                            fetchBruker()
                        }}
                    />
                    <Box
                        sx={{
                            height: '100%',
                            color: 'common.white',
                            paddingTop: '3rem',
                            paddingLeft: '6rem',
                            paddingRight: '1rem',
                            backgroundColor: '#35271E',
                        }}>
                        {brukerInformasjon?.foedselsnummer ? (
                            <div>
                                <HeaderText
                                    iconLeft={
                                        <AccountCircleOutlinedIcon
                                            fontSize='large'
                                            sx={{
                                                marginRight: '1rem',
                                                marginLeft: '-3.25rem',
                                                color: 'common.white',
                                            }}
                                        />
                                    }
                                    chosen={true}
                                    bruker={true}
                                    isLoggedIn={
                                        !!brukerInformasjon.foedselsnummer
                                    }
                                    header={
                                        <FormattedMessage
                                            id={'component.header.logged-in-as'}
                                        />
                                    }
                                    content={brukerInformasjon.foedselsnummer}
                                    openDialogFunction={() => ({})}
                                />
                                <HeaderText
                                    iconLeft={null}
                                    chosen={true}
                                    bruker={true}
                                    isLoggedIn={
                                        !!brukerInformasjon.foedselsnummer
                                    }
                                    header={
                                        <FormattedMessage
                                            id={'component.header.log-out'}
                                        />
                                    }
                                    content={
                                        <FormattedMessage
                                            id={'component.header.log-out.text'}
                                        />
                                    }
                                    openDialogFunction={() => ({})}
                                />
                            </div>
                        ) : (
                            <HeaderText
                                iconLeft={
                                    <AccountCircleOutlinedIcon
                                        fontSize='large'
                                        sx={{
                                            marginRight: '1rem',
                                            marginLeft: '-3.25rem',
                                            color: 'common.white',
                                        }}
                                    />
                                }
                                chosen={true}
                                bruker={true}
                                header={
                                    <FormattedMessage
                                        id={'component.header.log-in'}
                                    />
                                }
                                content={
                                    <FormattedMessage
                                        id={'component.header.log-in.text'}
                                    />
                                }
                                openDialogFunction={() => ({})}
                            />
                        )}
                    </Box>
                </Box>
            </Dialog>
            <Fade
                in={getProgress() !== 100}
                style={{
                    transitionDelay: getProgress() !== 100 ? '0ms' : '1000ms',
                }}>
                <LinearProgress
                    sx={{
                        position: 'absolute',
                        bottom: '0',
                        right: '0',
                        left: '0',
                    }}
                    variant='determinate'
                    value={getProgress()}
                />
            </Fade>
        </Container>
    )
}

export default LogoHeader
